import type { PointerEvent as ReactPointerEvent } from 'react';
import type { BoardDoc } from '../../doc/BoardDoc';
import type { Point } from '../../doc/geometry';
import { paneAt } from '../paneRegistry';
import type { PaneStore } from '../paneStore';
import { screenToWorld } from '../viewport/camera';

/**
 * Dragging a tile out of the cutout drawer. Pointer events rather than HTML drag and drop, so it
 * works with a finger and a pen: a mouse or pen drags once it has moved a little, a finger after
 * a short hold (a moving finger scrolls the drawer instead). A ghost of the cutout follows the
 * pointer, at the size it will have on the board while it is over one. Released over a canvas
 * it lands there; over a folder of the drawer it is filed there.
 */

export type TileDrop =
  | { kind: 'board'; board: BoardDoc; pane: PaneStore; el: Element; world: Point }
  /** A folder of the drawer ('' = unsorted). */
  | { kind: 'folder'; folderKey: string };

export interface TileDragOptions {
  /** Preview image (object URL), if loaded. */
  preview: string | undefined;
  /** Size on the board, in board units. */
  w: number;
  h: number;
  /** The folder under the pointer changed (null: none). */
  onHover(folderKey: string | null): void;
  onDrop(drop: TileDrop): void;
}

const MOUSE_SLOP = 5;
const TOUCH_SLOP = 8;
const HOLD_MS = 280;
const GHOST_PX = 88;

/** Folder chips (and the grid, for the folder it shows) mark themselves with this attribute. */
export const FOLDER_ATTR = 'data-drawer-folder';

/** What a pointer at this spot would drop onto. */
function targetAt(x: number, y: number): TileDrop | null {
  const hit = document.elementFromPoint(x, y);
  if (!hit) return null;
  const folder = hit.closest<HTMLElement>(`[${FOLDER_ATTR}]`);
  if (folder) return { kind: 'folder', folderKey: folder.getAttribute(FOLDER_ATTR) ?? '' };
  const viewport = hit.closest('.viewport');
  const target = viewport ? paneAt(x, y) : null;
  if (!viewport || !target) return null;
  const r = viewport.getBoundingClientRect();
  const world = screenToWorld(target.pane.getState().camera, { x: x - r.left, y: y - r.top });
  return { kind: 'board', board: target.board, pane: target.pane, el: target.el, world };
}

/** Call from a tile's onPointerDown. */
export function beginTileDrag(e: ReactPointerEvent<HTMLElement>, opts: TileDragOptions): void {
  if (e.button !== 0 || (e.target as Element).closest('button, input')) return;
  const pointerId = e.pointerId;
  const touch = e.pointerType === 'touch';
  const start = { x: e.clientX, y: e.clientY };
  let last = start;
  let ghost: HTMLDivElement | null = null;
  let hoverKey: string | null = null;
  let holdTimer: ReturnType<typeof setTimeout> | undefined;

  const setHover = (key: string | null) => {
    if (key === hoverKey) return;
    hoverKey = key;
    opts.onHover(key);
  };

  const paint = () => {
    if (!ghost) return;
    const t = targetAt(last.x, last.y);
    setHover(t?.kind === 'folder' ? t.folderKey : null);
    let w = GHOST_PX;
    let h = GHOST_PX;
    let rot = 0;
    const longest = Math.max(opts.w, opts.h, 1);
    if (t?.kind === 'board') {
      // true to size over a canvas, within reason
      const cam = t.pane.getState().camera;
      const k = Math.min(cam.zoom, 1600 / longest);
      w = Math.max(16, opts.w * k);
      h = Math.max(16, opts.h * k);
      rot = cam.rot;
    } else {
      w = (GHOST_PX * opts.w) / longest;
      h = (GHOST_PX * opts.h) / longest;
    }
    ghost.classList.toggle('can-drop', t !== null);
    ghost.style.width = `${w}px`;
    ghost.style.height = `${h}px`;
    ghost.style.transform = `translate(${last.x - w / 2}px, ${last.y - h / 2}px) rotate(${rot}deg)`;
  };

  const startDrag = () => {
    clearTimeout(holdTimer);
    if (ghost) return;
    ghost = document.createElement('div');
    ghost.className = 'drawer-ghost';
    if (opts.preview) ghost.style.backgroundImage = `url("${opts.preview}")`;
    document.body.appendChild(ghost);
    document.body.classList.add('is-drawer-dragging');
    if (touch) navigator.vibrate?.(8);
    paint();
  };

  const finish = (drop: boolean) => {
    clearTimeout(holdTimer);
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onCancel);
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('keydown', onKey, true);
    const dragged = ghost !== null;
    ghost?.remove();
    ghost = null;
    document.body.classList.remove('is-drawer-dragging');
    setHover(null);
    if (!dragged) return;
    // the click that ends a drag is not a click on the tile
    const swallow = (ev: MouseEvent) => {
      ev.stopPropagation();
      ev.preventDefault();
    };
    window.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => window.removeEventListener('click', swallow, true), 0);
    if (!drop) return;
    const t = targetAt(last.x, last.y);
    if (t) opts.onDrop(t);
  };

  const onMove = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    last = { x: ev.clientX, y: ev.clientY };
    if (!ghost) {
      const moved = Math.hypot(last.x - start.x, last.y - start.y);
      // a finger that moves before the hold is over is scrolling the drawer
      if (touch) {
        if (moved > TOUCH_SLOP) finish(false);
        return;
      }
      if (moved < MOUSE_SLOP) return;
      startDrag();
    }
    paint();
  };
  const onUp = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    last = { x: ev.clientX, y: ev.clientY };
    finish(true);
  };
  const onCancel = (ev: PointerEvent) => {
    if (ev.pointerId === pointerId) finish(false);
  };
  // once a finger carries a cutout the page must not scroll under it
  const onTouchMove = (ev: TouchEvent) => {
    if (ghost && ev.cancelable) ev.preventDefault();
  };
  const onKey = (ev: KeyboardEvent) => {
    if (ev.key !== 'Escape' || !ghost) return;
    ev.stopPropagation();
    finish(false);
  };

  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onCancel);
  window.addEventListener('touchmove', onTouchMove, { passive: false });
  window.addEventListener('keydown', onKey, true);
  if (touch) holdTimer = setTimeout(startDrag, HOLD_MS);
}

// ---- the other direction: board items dragged onto an open drawer ---------------------

/** The drawer (and the folder of it) under a point, for a move gesture that ends there. */
export function drawerDropAt(x: number, y: number): { el: HTMLElement; folderKey: string | null } | null {
  const hit = document.elementFromPoint(x, y);
  const el = hit?.closest<HTMLElement>('.drawer');
  if (!el) return null;
  const folder = hit?.closest<HTMLElement>(`[${FOLDER_ATTR}]`);
  return { el, folderKey: folder ? (folder.getAttribute(FOLDER_ATTR) ?? '') : null };
}

/** Raised on the drawer element while board items hover over it, and when they are dropped. */
export const DRAWER_HOVER_EVENT = 'vds:drawer-hover';
export const DRAWER_DROP_EVENT = 'vds:drawer-drop';

export interface DrawerDropDetail {
  board: BoardDoc;
  pane: PaneStore;
  ids: string[];
  folderKey: string | null;
  /** Runs once the cutout is saved (a duplicating drag removes its copies then). */
  after?: () => void;
}
